import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Alert, Button, CircularProgress, FormControl, InputLabel, MenuItem, Select } from '@mui/material';
import { toast } from 'react-toastify';
import BaseModal from '../../../shared/components/base.modal';
import apiClient from '../../../shared/auth.interceptor';
import { errorToast } from '../../../shared/toast.service';

/** A scheduled date with no posting, as the API lists it (display values in company formats). */
export interface MissedDate {
  date: string;
  periodStart: string;
  periodEnd: string;
  journalDate: string;
  display: { date: string; period: string; journalDate: string };
}

export interface MissedPreview extends MissedDate {
  amount: number;
  referenceBalance: number;
  reversed: boolean;
  display: MissedDate['display'] & {
    referenceBalance: string;
    amount: string;
    percentage: string;
    debitAccount?: string;
    creditAccount?: string;
  };
}

interface PostMissedModalProps {
  open: boolean;
  templateId: string;
  templateName: string;
  onClose: () => void;
  onPosted?: () => void;
}

const serverMessage = (error: unknown, fallback: string) =>
  (axios.isAxiosError(error) && error.response?.data?.message) || fallback;

const PostMissedModal: React.FC<PostMissedModalProps> = ({ open, templateId, templateName, onClose, onPosted }) => {
  const [dates, setDates] = useState<MissedDate[] | null>(null);
  const [selected, setSelected] = useState('');
  const [preview, setPreview] = useState<MissedPreview | null>(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [posting, setPosting] = useState(false);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setDates(null);
    setSelected('');
    setPreview(null);
    apiClient
      .get<MissedDate[]>(`/v1/journal/template/${templateId}/missed`)
      .then((response) => !cancelled && setDates(response.data || []))
      .catch((error) => {
        if (cancelled) return;
        errorToast(serverMessage(error, 'Could not load missed dates'));
        onClose();
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, templateId]);

  const choose = async (date: string) => {
    setSelected(date);
    setPreview(null);
    if (!date) return;
    setLoadingPreview(true);
    try {
      const response = await apiClient.post<MissedPreview>(`/v1/journal/template/${templateId}/missed/preview`, { date });
      setPreview(response.data);
    } catch (error) {
      errorToast(serverMessage(error, 'Could not calculate that date'));
      setSelected('');
    } finally {
      setLoadingPreview(false);
    }
  };

  const post = async () => {
    if (!preview || posting) return;
    setPosting(true);
    try {
      const response = await apiClient.post(`/v1/journal/template/${templateId}/missed`, {
        date: preview.date,
        name: templateName
      });
      toast.success(`Journal entry ${response.data?.txn || ''} posted for ${preview.display.date}.`);
      onPosted?.();
      onClose();
    } catch (error) {
      errorToast(serverMessage(error, 'Could not post the missed transaction'));
    } finally {
      setPosting(false);
    }
  };

  const row = (label: string, value?: string) => (
    <tr>
      <td className="pe-3 py-1 text-nowrap" style={{ color: '#6c757d' }}>
        {label}
      </td>
      <td className="py-1">{value}</td>
    </tr>
  );

  return (
    <BaseModal open={open} handleClose={posting ? () => undefined : onClose} width={520}>
      <h2 className="fw-bold my-3">Post Missed Transaction</h2>
      <p className="mb-3" style={{ textAlign: 'left' }}>
        Posts a scheduled date that never posted, as if it had run that day: the same period and journal date, with
        the next journal number. The amount uses the books as they are today. This uses the saved template.
      </p>
      {dates === null ? (
        <CircularProgress size={28} className="my-3" />
      ) : dates.length === 0 ? (
        <Alert severity="success" sx={{ textAlign: 'left' }}>
          No missed transactions since the start date.
        </Alert>
      ) : (
        <FormControl fullWidth size="small" sx={{ textAlign: 'left' }}>
          <InputLabel id="missed-date-label">Scheduled date</InputLabel>
          <Select
            labelId="missed-date-label"
            label="Scheduled date"
            value={selected}
            disabled={posting}
            onChange={(e) => void choose(e.target.value)}
            MenuProps={{ PaperProps: { sx: { maxHeight: 300 } } }}
          >
            {dates.map((d) => (
              <MenuItem key={d.date} value={d.date}>
                {d.display.date}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      )}
      {loadingPreview && <CircularProgress size={28} className="my-3" />}
      {preview && (
        <table className="my-3" style={{ textAlign: 'left', width: '100%' }}>
          <tbody>
            {row('Period', preview.display.period)}
            {row('Journal date', preview.display.journalDate)}
            {row('Reference balance', preview.display.referenceBalance)}
            {row('Percentage', preview.display.percentage)}
            {row('Amount', preview.display.amount)}
            {row('Debit', preview.display.debitAccount)}
            {row('Credit', preview.display.creditAccount)}
          </tbody>
        </table>
      )}
      {preview?.reversed && (
        <Alert severity="info" sx={{ textAlign: 'left' }}>
          The calculation is negative, so debit and credit are reversed.
        </Alert>
      )}
      <div className="d-flex justify-content-between my-3">
        <Button onClick={onClose} disabled={posting} variant="outlined" sx={{ mt: 2, width: '136px' }}>
          Cancel
        </Button>
        <Button onClick={post} disabled={!preview || posting} variant="contained" sx={{ mt: 2, width: '136px' }}>
          {posting ? 'Posting…' : 'Post'}
        </Button>
      </div>
    </BaseModal>
  );
};

export default PostMissedModal;
